import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Lock, Plus, Trash2, Pencil, Loader2, ChevronDown, ChevronRight, AlertTriangle, RefreshCw } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  fetchJson, recomputeYear, yearOptions, errorMessage, money, MONTHS,
  SAFE_HARBOR_LABELS, AFFORDABILITY_LABELS, LNAP_LABELS,
} from "./shared/configShared";
import { Help, TermLabel, Explained, CodeHelp } from "./shared/Help";
import type { GlossaryKey } from "./shared/acaGlossary";

interface ACAAffordabilityProps {
  clientId?: number;
}

const EMPLOYMENT_TYPES = ["Full-Time", "Part-Time", "Variable Hour", "Seasonal"];

interface PolicyDraft {
  id?: number;
  name: string;
  safeHarbor: "fpl" | "rate_of_pay" | "w2";
  isDefault: boolean;
  employeeClassIds: number[];
  compensationTypes: ("hourly" | "salary")[];
  employmentTypes: string[];
}
const emptyPolicy = (): PolicyDraft => ({ name: "General", safeHarbor: "fpl", isDefault: false, employeeClassIds: [], compensationTypes: [], employmentTypes: [] });

interface OverrideDraft {
  id?: number;
  name: string;
  effectiveStartMonth: number;
  effectiveEndMonth: number;
  line15EmployeeCost: string;
  overrideReason: string;
  employeeClassIds: number[];
  compensationTypes: ("hourly" | "salary")[];
}
const emptyOverride = (): OverrideDraft => ({ name: "Override", effectiveStartMonth: 1, effectiveEndMonth: 12, line15EmployeeCost: "", overrideReason: "", employeeClassIds: [], compensationTypes: [] });

const affordabilityColor: Record<string, string> = {
  affordable: "bg-green-500",
  not_affordable: "bg-red-500",
  pending_data: "bg-amber-400",
  not_applicable: "bg-gray-300 dark:bg-gray-700",
};
const harborTerm = (h: string | undefined | null): GlossaryKey => h === "rate_of_pay" ? "rateOfPaySafeHarbor" : h === "w2" ? "w2SafeHarbor" : "fplSafeHarbor";
const affordTerm = (s: string | undefined | null): GlossaryKey => s === "pending_data" ? "pendingData" : s === "not_applicable" ? "notApplicable" : s === "mixed" ? "affordabilityMixed" : "affordability";

const affordabilityBadge: Record<string, string> = {
  affordable: "bg-green-500 text-white",
  not_affordable: "bg-red-500 text-white",
  pending_data: "bg-amber-400 text-black",
  not_applicable: "",
  mixed: "bg-orange-500 text-white",
};

export default function ACAAffordability({ clientId: propClientId }: ACAAffordabilityProps) {
  const { user } = useAuth();
  const clientId = propClientId ?? (user as any)?.clientId;
  const currentYear = new Date().getFullYear();
  const [taxYear, setTaxYear] = useState(currentYear - 1);
  const { toast } = useToast();
  const [policyDraft, setPolicyDraft] = useState<PolicyDraft | null>(null);
  const [overrideDraft, setOverrideDraft] = useState<OverrideDraft | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [previewFilter, setPreviewFilter] = useState<string>("all");

  const q = (key: string, url: string, enabled = !!clientId) => ({ queryKey: [key, clientId, taxYear], queryFn: () => fetchJson(url), enabled });

  const { data: policies = [] } = useQuery<any[]>(q("/api/aca/affordability-policies", `/api/aca/affordability-policies?clientId=${clientId}&taxYear=${taxYear}`));
  const { data: overrides = [] } = useQuery<any[]>(q("/api/aca/line15-cost-rules", `/api/aca/line15-cost-rules?clientId=${clientId}&taxYear=${taxYear}`));
  const { data: constants } = useQuery<any>({ queryKey: ["/api/aca/constants", taxYear], queryFn: () => fetchJson(`/api/aca/constants?year=${taxYear}`) });
  const { data: year, isLoading: yearLoading, isFetching: yearFetching } = useQuery<any>(q("/api/aca/year", `/api/aca/year?clientId=${clientId}&taxYear=${taxYear}`));
  const { data: classes = [] } = useQuery<any[]>({
    queryKey: ["/api/org/employee-classes", clientId],
    queryFn: () => fetchJson(`/api/org/employee-classes?ownerType=client&ownerId=${clientId}`),
    enabled: !!clientId,
  });
  const { data: reportingYears = [] } = useQuery<any[]>({
    queryKey: ["/api/aca/reporting-years", clientId],
    queryFn: () => fetchJson(`/api/aca/reporting-years?clientId=${clientId}`),
    enabled: !!clientId,
  });
  const isLocked = !!reportingYears.find((y) => y.taxYear === taxYear)?.isLocked;
  const classNameById = useMemo(() => new Map<number, string>(classes.map((c: any) => [Number(c.id), String(c.name)])), [classes]);

  const afterWrite = async () => {
    await recomputeYear(clientId, taxYear);
    queryClient.invalidateQueries({ queryKey: ["/api/aca/affordability-policies"] });
    queryClient.invalidateQueries({ queryKey: ["/api/aca/line15-cost-rules"] });
  };
  const onError = (title: string) => (e: any) => toast({ title, description: errorMessage(e), variant: "destructive" });

  const savePolicy = useMutation({
    mutationFn: async (d: PolicyDraft) => {
      const body = {
        clientId, taxYear, name: d.name.trim() || "General", safeHarbor: d.safeHarbor, isDefault: d.isDefault,
        segmentation: { employeeClassIds: d.employeeClassIds, compensationTypes: d.compensationTypes, employmentTypes: d.employmentTypes },
      };
      const res = d.id ? await apiRequest("PUT", `/api/aca/affordability-policies/${d.id}?clientId=${clientId}`, body) : await apiRequest("POST", "/api/aca/affordability-policies", body);
      await res.json();
      await afterWrite();
    },
    onSuccess: () => { setPolicyDraft(null); toast({ title: "Safe harbor policy saved", description: "Results recalculated." }); },
    onError: onError("Policy not saved"),
  });
  const deletePolicy = useMutation({
    mutationFn: async (id: number) => { await apiRequest("DELETE", `/api/aca/affordability-policies/${id}?clientId=${clientId}`); await afterWrite(); },
    onSuccess: () => toast({ title: "Policy removed" }),
    onError: onError("Could not remove policy"),
  });

  const saveOverride = useMutation({
    mutationFn: async (d: OverrideDraft) => {
      const body = {
        clientId, taxYear, name: d.name.trim() || "Override",
        effectiveStartMonth: Number(d.effectiveStartMonth), effectiveEndMonth: Number(d.effectiveEndMonth),
        line15EmployeeCost: String(Number(String(d.line15EmployeeCost).replace(/[^0-9.]/g, "")) || 0),
        overrideReason: d.overrideReason.trim(),
        segmentation: { employeeClassIds: d.employeeClassIds, compensationTypes: d.compensationTypes },
      };
      const res = d.id ? await apiRequest("PUT", `/api/aca/line15-cost-rules/${d.id}?clientId=${clientId}`, body) : await apiRequest("POST", "/api/aca/line15-cost-rules", body);
      await res.json();
      await afterWrite();
    },
    onSuccess: () => { setOverrideDraft(null); toast({ title: "Override saved", description: "Results recalculated." }); },
    onError: onError("Override not saved"),
  });
  const deleteOverride = useMutation({
    mutationFn: async (id: number) => { await apiRequest("DELETE", `/api/aca/line15-cost-rules/${id}?clientId=${clientId}`); await afterWrite(); },
    onSuccess: () => toast({ title: "Override removed" }),
    onError: onError("Could not remove override"),
  });
  const recompute = useMutation({
    mutationFn: () => recomputeYear(clientId, taxYear),
    onSuccess: () => toast({ title: "Recalculated" }),
    onError: onError("Recalculation failed"),
  });

  const employees: any[] = year?.result?.employees ?? [];

  // Employee Required Contribution summary — straight from engine month records.
  const contributionRows = useMemo(() => employees.map((e) => {
    const offered = e.months.filter((m: any) => m.offered && m.line15 != null);
    const costs = offered.map((m: any) => Number(m.line15));
    const plans = Array.from(new Set(e.months.map((m: any) => m.offerPlanName).filter(Boolean))) as string[];
    const sources = Array.from(new Set(e.months.map((m: any) => m.line15Source).filter(Boolean))) as string[];
    const harbor = e.months.find((m: any) => m.safeHarbor)?.safeHarbor as string | undefined;
    return {
      employeeId: e.employeeId, name: `${e.lastName}, ${e.firstName}`, plans, sources, harbor,
      min: costs.length ? Math.min(...costs) : null, max: costs.length ? Math.max(...costs) : null,
      offeredMonths: e.summary.offeredMonths, status: e.summary.status,
    };
  }), [employees]);

  const previewRows = useMemo(() => employees.filter((e) => previewFilter === "all" ? true : previewFilter === "issues" ? (e.summary.status === "not_affordable" || e.summary.status === "mixed" || e.summary.status === "pending_data") : e.summary.status === previewFilter), [employees, previewFilter]);

  const toggleIn = <T,>(arr: T[], v: T): T[] => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const segLabel = (seg: any) => {
    const parts: string[] = [];
    if (seg?.employeeClassIds?.length) parts.push(`Class: ${seg.employeeClassIds.map((id: number) => classNameById.get(Number(id)) ?? `#${id}`).join(", ")}`);
    if (seg?.compensationTypes?.length) parts.push(`Pay: ${seg.compensationTypes.join(", ")}`);
    if (seg?.employmentTypes?.length) parts.push(`Type: ${seg.employmentTypes.join(", ")}`);
    return parts.length ? parts.join(" · ") : "All employees";
  };

  if (!clientId) return <Alert><AlertDescription>No client selected.</AlertDescription></Alert>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Affordability</h2>
          <p className="text-sm text-muted-foreground">Safe harbor assignment, employee required contribution and month-by-month results for {taxYear}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(taxYear)} onValueChange={(v) => { setTaxYear(Number(v)); setExpanded(null); }}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>{yearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={() => recompute.mutate()} disabled={recompute.isPending || yearFetching}>
            {recompute.isPending || yearFetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Recalculate
          </Button>
        </div>
      </div>

      {isLocked && <Alert><Lock className="h-4 w-4" /><AlertDescription>{taxYear} is finalized. Unlock it on the History tab before changing safe harbors or overrides.</AlertDescription></Alert>}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Lock className="h-4 w-4" /> Statutory values for plan years beginning in {taxYear}</CardTitle>
          <CardDescription>Maintained by Administr from IRS/HHS guidance ({constants?.source === "superadmin_override" ? "superadmin override" : "statutory table"}). Not editable.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Stat term="affordabilityPercent" label="Affordability percentage" value={constants?.affordabilityPercent != null ? `${constants.affordabilityPercent}%` : "—"} />
          <Stat term="fpl" label="Federal poverty line (single)" value={constants?.fplAnnualSingle != null ? `$${Number(constants.fplAnnualSingle).toLocaleString()}` : "—"} hint={constants?.fplGuidelineYear ? `${constants.fplGuidelineYear} HHS guideline` : undefined} />
          <Stat term="fplSafeHarbor" label="FPL safe harbor (monthly)" value={money(constants?.fplSafeHarborMonthly)} hint="FPL ÷ 12 × percentage" />
          <Stat term="rateOfPaySafeHarbor" label="Rate of pay basis" value="Hourly rate × 130 h, or salary ÷ 12" hint="Basis follows each employee's compensation type" />
          <p className="col-span-full text-xs text-muted-foreground">
            The percentage is applied by the calendar year in which each plan year begins. A non-calendar plan year can use two percentages within one tax year; the engine picks the right one per month from the plan's effective date. <Help term="planYear" />
          </p>
        </CardContent>
      </Card>

      <Tabs defaultValue="policies" className="space-y-4">
        <TabsList>
          <TabsTrigger value="policies"><TermLabel term="safeHarbor">Safe Harbor Policies</TermLabel></TabsTrigger>
          <TabsTrigger value="contribution"><TermLabel term="employeeRequiredContribution">Employee Required Contribution</TermLabel></TabsTrigger>
          <TabsTrigger value="preview"><TermLabel term="affordability">Employee Preview</TermLabel></TabsTrigger>
        </TabsList>

        {/* ---------------- Safe harbor policies ---------------- */}
        <TabsContent value="policies" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle><TermLabel term="safeHarbor">Safe harbor assignment</TermLabel></CardTitle>
                <CardDescription>One default policy, plus category policies applied consistently within a reasonable employee category.</CardDescription>
              </div>
              <Button onClick={() => setPolicyDraft({ ...emptyPolicy(), isDefault: policies.length === 0 })} disabled={isLocked}><Plus className="h-4 w-4 mr-2" />Add policy</Button>
            </CardHeader>
            <CardContent>
              {policies.length === 0 ? (
                <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>No safe harbor policy for {taxYear}. Every employee is "Pending Data" until one is assigned.</AlertDescription></Alert>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead><TermLabel term="safeHarbor">Safe harbor</TermLabel></TableHead>
                      <TableHead><TermLabel term="safeHarborCategory">Applies to</TermLabel></TableHead>
                      <TableHead><TermLabel term="safeHarborCategory">Default</TermLabel></TableHead>
                      <TableHead className="w-24" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {policies.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell><Explained term={harborTerm(p.safeHarbor)}><span className="underline decoration-dotted">{SAFE_HARBOR_LABELS[p.safeHarbor] ?? p.safeHarbor}</span></Explained></TableCell>
                        <TableCell className="text-sm text-muted-foreground">{segLabel(p.segmentation)}</TableCell>
                        <TableCell>{p.isDefault ? <Badge>Default</Badge> : null}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" disabled={isLocked} onClick={() => setPolicyDraft({
                            id: p.id, name: p.name, safeHarbor: p.safeHarbor, isDefault: !!p.isDefault,
                            employeeClassIds: p.segmentation?.employeeClassIds ?? [], compensationTypes: p.segmentation?.compensationTypes ?? [], employmentTypes: p.segmentation?.employmentTypes ?? [],
                          })}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" disabled={isLocked || deletePolicy.isPending} onClick={() => deletePolicy.mutate(p.id)}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-muted-foreground">
                <div><span className="font-medium text-foreground"><TermLabel term="fplSafeHarbor">Federal Poverty Line.</TermLabel></span> Affordable when the self-only required contribution is at or below {money(constants?.fplSafeHarborMonthly)}/month. No wage data needed; qualifies for the 1A Qualifying Offer code.</div>
                <div><span className="font-medium text-foreground"><TermLabel term="rateOfPaySafeHarbor">Rate of Pay.</TermLabel></span> Hourly: lowest hourly rate in the month × 130 × {constants?.affordabilityPercent ?? "—"}%. Salaried: monthly salary × {constants?.affordabilityPercent ?? "—"}%. Uses the effective-dated compensation on each employee record; unavailable if salary was reduced during the year.</div>
                <div><span className="font-medium text-foreground"><TermLabel term="w2SafeHarbor">Form W-2.</TermLabel></span> Box 1 wages × {constants?.affordabilityPercent ?? "—"}% prorated to months employed. Shows <em>Pending Data</em> until final Box 1 wages are entered on the Employees tab.</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- Employee required contribution ---------------- */}
        <TabsContent value="contribution" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle><TermLabel term="employeeRequiredContribution">Employee Required Contribution (Form 1095-C Line 15)</TermLabel></CardTitle>
              <CardDescription>
                Derived automatically per employee and per month: the lowest-cost self-only plan providing minimum value that the employee is eligible for (eligibility groups, waiting period, entry date), priced from the plan-year rates in effect that month, minus the employer contribution for the employee's group. Floored at $0.00. Line 15 is only populated when Line 14 requires it.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {yearLoading ? <div className="text-sm text-muted-foreground">Loading…</div> : contributionRows.length === 0 ? (
                <div className="text-sm text-muted-foreground">No employees in the {taxYear} population.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead><TermLabel term="lowestCostPlan">Lowest-cost MV plan(s)</TermLabel></TableHead>
                      <TableHead className="text-right"><TermLabel term="line15">Monthly contribution</TermLabel></TableHead>
                      <TableHead><TermLabel term="offer">Months offered</TermLabel></TableHead>
                      <TableHead><TermLabel term="override">Source</TermLabel></TableHead>
                      <TableHead><TermLabel term="safeHarbor">Safe harbor</TermLabel></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contributionRows.map((r) => (
                      <TableRow key={r.employeeId}>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell className="text-sm">{r.plans.length ? r.plans.join(", ") : <span className="text-muted-foreground">No offer</span>}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.min == null ? "—" : r.min === r.max ? money(r.min) : `${money(r.min)} – ${money(r.max)}`}</TableCell>
                        <TableCell>{r.offeredMonths}/12</TableCell>
                        <TableCell>{r.sources.map((s) => <Explained key={s} term={s === "manual_override" ? "override" : "employeeRequiredContribution"}><Badge variant={s === "manual_override" ? "destructive" : "outline"} className="mr-1">{s === "manual_override" ? "Override" : "Derived"}</Badge></Explained>)}</TableCell>
                        <TableCell className="text-sm">{r.harbor ? SAFE_HARBOR_LABELS[r.harbor] : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base"><TermLabel term="override">Advanced: Employee Required Contribution overrides</TermLabel></CardTitle>
                <CardDescription>Replaces the derived amount for the months and segment covered. Every override needs a reason and is audited.</CardDescription>
              </div>
              <Button variant="outline" onClick={() => setOverrideDraft(emptyOverride())} disabled={isLocked}><Plus className="h-4 w-4 mr-2" />Add override</Button>
            </CardHeader>
            <CardContent>
              {overrides.length === 0 ? <div className="text-sm text-muted-foreground">No overrides. Amounts are fully derived.</div> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Months</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Applies to</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="w-24" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {overrides.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell className="font-medium">{o.name}</TableCell>
                        <TableCell>{MONTHS[(o.effectiveStartMonth ?? 1) - 1]} – {MONTHS[(o.effectiveEndMonth ?? 12) - 1]}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(o.line15EmployeeCost != null ? Number(o.line15EmployeeCost) : null)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{segLabel(o.segmentation)}</TableCell>
                        <TableCell className="text-sm">{o.overrideReason || <span className="text-destructive">Missing reason</span>}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" disabled={isLocked} onClick={() => setOverrideDraft({
                            id: o.id, name: o.name ?? "Override", effectiveStartMonth: o.effectiveStartMonth ?? 1, effectiveEndMonth: o.effectiveEndMonth ?? 12,
                            line15EmployeeCost: o.line15EmployeeCost ?? "", overrideReason: o.overrideReason ?? "",
                            employeeClassIds: o.segmentation?.employeeClassIds ?? [], compensationTypes: o.segmentation?.compensationTypes ?? [],
                          })}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" disabled={isLocked || deleteOverride.isPending} onClick={() => deleteOverride.mutate(o.id)}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- Employee preview ---------------- */}
        <TabsContent value="preview" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle><TermLabel term="affordability">Employee Preview</TermLabel></CardTitle>
                <CardDescription>Twelve monthly results per employee from the ACA engine{year?.meta?.computedAt ? ` (computed ${new Date(year.meta.computedAt).toLocaleString()}, v${year.meta.version})` : ""}.</CardDescription>
              </div>
              <Select value={previewFilter} onValueChange={setPreviewFilter}>
                <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All employees</SelectItem>
                  <SelectItem value="issues">Needs attention</SelectItem>
                  <SelectItem value="affordable">Affordable</SelectItem>
                  <SelectItem value="not_affordable">Not Affordable</SelectItem>
                  <SelectItem value="mixed">Mixed</SelectItem>
                  <SelectItem value="pending_data">Pending Data</SelectItem>
                  <SelectItem value="not_applicable">Not Applicable</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="p-0">
              {yearLoading ? <div className="p-6 text-sm text-muted-foreground">Loading…</div> : previewRows.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground">No employees match.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8" />
                      <TableHead>Employee</TableHead>
                      <TableHead><TermLabel term="affordability">Status</TermLabel></TableHead>
                      <TableHead><TermLabel term="affordability" text="One cell per month: green affordable, red not affordable, amber pending data, grey not applicable, dashed not employed. Hover a cell for the month.">Months</TermLabel></TableHead>
                      <TableHead><TermLabel term="safeHarbor">Safe harbor</TermLabel></TableHead>
                      <TableHead><TermLabel term="formRequired">1095-C</TermLabel></TableHead>
                      <TableHead><TermLabel term="exceptionWarning">Warnings</TermLabel></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRows.map((e) => {
                      const open = expanded === e.employeeId;
                      const harbor = e.months.find((m: any) => m.safeHarbor)?.safeHarbor;
                      const warnings = [...e.exceptions, ...e.months.flatMap((m: any) => m.exceptions)];
                      return (
                        <>
                          <TableRow key={e.employeeId} className="cursor-pointer" onClick={() => setExpanded(open ? null : e.employeeId)}>
                            <TableCell>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                            <TableCell className="font-medium">{e.lastName}, {e.firstName}<div className="text-xs text-muted-foreground">{e.employmentType || "—"}</div></TableCell>
                            <TableCell><Explained term={affordTerm(e.summary.status)}><Badge className={affordabilityBadge[e.summary.status]} variant={e.summary.status === "not_applicable" ? "secondary" : "default"}>{AFFORDABILITY_LABELS[e.summary.status] ?? e.summary.status}</Badge></Explained></TableCell>
                            <TableCell>
                              <div className="flex gap-0.5">
                                {e.months.map((m: any) => (
                                  <span key={m.month} title={`${MONTHS[m.month - 1]}: ${AFFORDABILITY_LABELS[m.affordability] ?? m.affordability}${m.lnapReason ? " · non-assessment period" : ""}${!m.employed ? " · not employed" : ""}`}
                                    className={`inline-block h-3 w-3 rounded-sm ${m.employed ? affordabilityColor[m.affordability] : "bg-transparent border border-dashed border-gray-300"}`} />
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{harbor ? <Explained term={harborTerm(harbor)}><span className="underline decoration-dotted">{SAFE_HARBOR_LABELS[harbor]}</span></Explained> : "—"}</TableCell>
                            <TableCell className="text-sm">{e.formRequired === "full_time" ? "Required (full-time)" : e.formRequired === "self_insured_enrolled" ? "Required (self-insured)" : <span className="text-muted-foreground">Not required</span>}</TableCell>
                            <TableCell>{warnings.length ? <Explained term={warnings.some((w: any) => w.severity === "blocking") ? "exceptionBlocking" : "exceptionWarning"}><Badge variant={warnings.some((w: any) => w.severity === "blocking") ? "destructive" : "outline"}>{warnings.length}</Badge></Explained> : null}</TableCell>
                          </TableRow>
                          {open && (
                            <TableRow key={`${e.employeeId}-detail`}>
                              <TableCell colSpan={7} className="bg-muted/30 p-4">
                                <MonthDetail employee={e} />
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ---------------- Policy dialog ---------------- */}
      <Dialog open={!!policyDraft} onOpenChange={(o) => !o && setPolicyDraft(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{policyDraft?.id ? "Edit safe harbor policy" : "New safe harbor policy"}</DialogTitle>
            <DialogDescription>Leave every category blank to apply to all employees.</DialogDescription>
          </DialogHeader>
          {policyDraft && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>Policy name</Label>
                <Input value={policyDraft.name} onChange={(e) => setPolicyDraft({ ...policyDraft, name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label><TermLabel term="safeHarbor">Safe harbor method</TermLabel></Label>
                <Select value={policyDraft.safeHarbor} onValueChange={(v) => setPolicyDraft({ ...policyDraft, safeHarbor: v as PolicyDraft["safeHarbor"] })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fpl">Federal Poverty Line</SelectItem>
                    <SelectItem value="rate_of_pay">Rate of Pay (hourly vs salaried follows the employee's compensation type)</SelectItem>
                    <SelectItem value="w2">Form W-2 (Pending Data until final Box 1 wages are entered)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="isDefault" checked={policyDraft.isDefault} onCheckedChange={(v) => setPolicyDraft({ ...policyDraft, isDefault: !!v })} />
                <Label htmlFor="isDefault"><TermLabel term="safeHarborCategory">Default policy for employees not matched by a category policy</TermLabel></Label>
              </div>
              <Collapsible defaultOpen={policyDraft.employeeClassIds.length + policyDraft.compensationTypes.length + policyDraft.employmentTypes.length > 0}>
                <CollapsibleTrigger asChild><Button variant="ghost" size="sm" className="px-0 text-muted-foreground"><ChevronDown className="h-4 w-4 mr-1" />Employee category (optional)</Button></CollapsibleTrigger>
                <Help term="safeHarborCategory" className="ml-1" />
                <CollapsibleContent className="space-y-3 pt-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Compensation type</Label>
                    <div className="flex gap-4">
                      {(["hourly", "salary"] as const).map((t) => (
                        <label key={t} className="flex items-center gap-2 text-sm"><Checkbox checked={policyDraft.compensationTypes.includes(t)} onCheckedChange={() => setPolicyDraft({ ...policyDraft, compensationTypes: toggleIn(policyDraft.compensationTypes, t) })} />{t === "hourly" ? "Hourly" : "Salaried"}</label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Employment type</Label>
                    <div className="flex flex-wrap gap-4">
                      {EMPLOYMENT_TYPES.map((t) => (
                        <label key={t} className="flex items-center gap-2 text-sm"><Checkbox checked={policyDraft.employmentTypes.includes(t)} onCheckedChange={() => setPolicyDraft({ ...policyDraft, employmentTypes: toggleIn(policyDraft.employmentTypes, t) })} />{t}</label>
                      ))}
                    </div>
                  </div>
                  {classes.length > 0 && (
                    <div className="space-y-1">
                      <Label className="text-xs">Employee class</Label>
                      <div className="flex flex-wrap gap-4">
                        {classes.map((c: any) => (
                          <label key={c.id} className="flex items-center gap-2 text-sm"><Checkbox checked={policyDraft.employeeClassIds.includes(Number(c.id))} onCheckedChange={() => setPolicyDraft({ ...policyDraft, employeeClassIds: toggleIn(policyDraft.employeeClassIds, Number(c.id)) })} />{c.name}</label>
                        ))}
                      </div>
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPolicyDraft(null)}>Cancel</Button>
            <Button onClick={() => policyDraft && savePolicy.mutate(policyDraft)} disabled={savePolicy.isPending}>{savePolicy.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save & Recalculate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Override dialog ---------------- */}
      <Dialog open={!!overrideDraft} onOpenChange={(o) => !o && setOverrideDraft(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{overrideDraft?.id ? "Edit override" : "Employee Required Contribution override"}</DialogTitle>
            <DialogDescription>Use only when the derived amount is wrong for a documented reason (e.g. a plan not yet in the library).</DialogDescription>
          </DialogHeader>
          {overrideDraft && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>Name</Label>
                <Input value={overrideDraft.name} onChange={(e) => setOverrideDraft({ ...overrideDraft, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>From month</Label>
                  <Select value={String(overrideDraft.effectiveStartMonth)} onValueChange={(v) => setOverrideDraft({ ...overrideDraft, effectiveStartMonth: Number(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>To month</Label>
                  <Select value={String(overrideDraft.effectiveEndMonth)} onValueChange={(v) => setOverrideDraft({ ...overrideDraft, effectiveEndMonth: Number(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label><TermLabel term="employeeRequiredContribution">Monthly amount ($)</TermLabel></Label>
                  <Input type="number" step="0.01" min={0} value={overrideDraft.line15EmployeeCost} onChange={(e) => setOverrideDraft({ ...overrideDraft, line15EmployeeCost: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1">
                <Label><TermLabel term="override">Reason (required)</TermLabel></Label>
                <Textarea value={overrideDraft.overrideReason} onChange={(e) => setOverrideDraft({ ...overrideDraft, overrideReason: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Compensation type (optional)</Label>
                <div className="flex gap-4">
                  {(["hourly", "salary"] as const).map((t) => (
                    <label key={t} className="flex items-center gap-2 text-sm"><Checkbox checked={overrideDraft.compensationTypes.includes(t)} onCheckedChange={() => setOverrideDraft({ ...overrideDraft, compensationTypes: toggleIn(overrideDraft.compensationTypes, t) })} />{t === "hourly" ? "Hourly" : "Salaried"}</label>
                  ))}
                </div>
              </div>
              {classes.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">Employee class (optional)</Label>
                  <div className="flex flex-wrap gap-4">
                    {classes.map((c: any) => (
                      <label key={c.id} className="flex items-center gap-2 text-sm"><Checkbox checked={overrideDraft.employeeClassIds.includes(Number(c.id))} onCheckedChange={() => setOverrideDraft({ ...overrideDraft, employeeClassIds: toggleIn(overrideDraft.employeeClassIds, Number(c.id)) })} />{c.name}</label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDraft(null)}>Cancel</Button>
            <Button onClick={() => overrideDraft && saveOverride.mutate(overrideDraft)} disabled={saveOverride.isPending || !overrideDraft?.overrideReason.trim() || !overrideDraft?.line15EmployeeCost}>
              {saveOverride.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save & Recalculate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, hint, term }: { label: string; value: string; hint?: string; term?: GlossaryKey }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{term ? <TermLabel term={term}>{label}</TermLabel> : label}</div>
      <div className="font-semibold">{value}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function MonthDetail({ employee }: { employee: any }) {
  const warnings: any[] = [...employee.exceptions, ...employee.months.flatMap((m: any) => m.exceptions)];
  const fixLabel: Record<string, string> = {
    employees: "Employees tab", policies: "Policies tab", affordability: "Affordability tab", settings: "Settings tab", forms: "Forms tab",
    hr: "Employee record (HR)", benefits: "Benefits / plan links", plan_library: "Plan library",
  };
  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Month</TableHead>
            <TableHead><TermLabel term="fullTime">ACA status</TermLabel></TableHead>
            <TableHead className="text-right"><TermLabel term="hoursOfService">Hours</TermLabel></TableHead>
            <TableHead><TermLabel term="offer">Offer</TermLabel></TableHead>
            <TableHead><TermLabel term="line14">Line 14</TermLabel></TableHead>
            <TableHead className="text-right"><TermLabel term="line15">Line 15</TermLabel></TableHead>
            <TableHead><TermLabel term="line16">Line 16</TermLabel></TableHead>
            <TableHead className="text-right"><TermLabel term="affordability" text="The safe-harbor amount the employee required contribution must not exceed for the month. FPL: FPL / 12 x %. Rate of pay: lowest hourly rate x 130 x % or monthly salary x %. W-2: Box 1 / months employed x %.">Threshold</TermLabel></TableHead>
            <TableHead><TermLabel term="affordability">Affordability</TermLabel></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {employee.months.map((m: any) => (
            <TableRow key={m.month} className={!m.employed ? "text-muted-foreground" : ""}>
              <TableCell>{MONTHS[m.month - 1]}</TableCell>
              <TableCell className="text-sm">
                {!m.employed ? "Not employed" : m.acaStatus === "full_time" ? "Full-time" : m.acaStatus === "non_full_time" ? "Not full-time" : m.acaStatus === "measuring" ? "Measuring" : "Unknown"}
                {m.lnapReason && <div className="text-xs text-muted-foreground"><TermLabel term="lnap">Limited Non-Assessment Period</TermLabel><br />{LNAP_LABELS[m.lnapReason] ?? m.lnapReason}</div>}
              </TableCell>
              <TableCell className="text-right tabular-nums text-sm">{m.hours == null ? "—" : Number(m.hours).toFixed(1)}{m.hoursSource === "schedule_estimate" && <Explained term="hoursEstimated"><span className="text-xs text-amber-600 ml-1">est.</span></Explained>}</TableCell>
              <TableCell className="text-sm">{m.offered ? (m.offerPlanName || "Offered") : m.employed ? "None" : "—"}</TableCell>
              <TableCell className="font-mono text-sm">{m.line14 ? <CodeHelp code={m.line14} line={14} /> : <Explained term="exceptionBlocking"><span className="text-destructive">?</span></Explained>}</TableCell>
              <TableCell className="text-right tabular-nums text-sm">{m.line15 == null ? "" : money(m.line15)}{m.line15Source === "manual_override" && <Explained term="override"><span className="text-xs ml-1">*</span></Explained>}</TableCell>
              <TableCell className="font-mono text-sm">{m.employed ? <CodeHelp code={m.line16} line={16}>{m.line16 ?? "—"}</CodeHelp> : ""}{m.line16 === "2D" && <div className="text-xs text-muted-foreground font-sans">Non-assessment period</div>}</TableCell>
              <TableCell className="text-right tabular-nums text-sm">{m.threshold == null ? "" : money(m.threshold)}{m.safeHarbor && m.threshold != null && <div className="text-xs text-muted-foreground"><Explained term={harborTerm(m.safeHarbor)}><span className="underline decoration-dotted">{SAFE_HARBOR_LABELS[m.safeHarbor]}</span></Explained></div>}</TableCell>
              <TableCell><Explained term={affordTerm(m.affordability)}><span><span className={`inline-block h-2 w-2 rounded-full mr-1 ${affordabilityColor[m.affordability]}`} />{AFFORDABILITY_LABELS[m.affordability] ?? m.affordability}</span></Explained></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {warnings.length > 0 && (
        <div className="space-y-1">
          {warnings.map((w, i) => (
            <div key={i} className="text-sm flex gap-2">
              <Explained term={w.severity === "blocking" ? "exceptionBlocking" : "exceptionWarning"}><Badge variant={w.severity === "blocking" ? "destructive" : "outline"} className="shrink-0">{w.severity === "blocking" ? "Blocking" : w.severity === "warning" ? "Warning" : "Info"}</Badge></Explained>
              <span>{w.month ? `${MONTHS[w.month - 1]}: ` : ""}{w.message}{w.fix?.area && <span className="text-muted-foreground"> → Fix in {fixLabel[w.fix.area] ?? w.fix.area}{w.fix.hint ? ` (${w.fix.hint})` : ""}</span>}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
